import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronDown, ChevronUp, Search, Minus, Plus, Trash2 } from 'lucide-react'
import { redeemPointFormSchema, type RedeemPointFormData } from '../types'
import { useBranchOffices } from '@/modules/clients/hooks/useBranchOffices'
import { useConfig } from '../hooks/useConfig'
import type { Client } from '@/modules/clients/types'
import type { ClientStatus } from '@/modules/clients/types'

/** SKU -> quantity */
type QuantityMap = Record<string, number>

interface RedeemPointFormProps {
  client: Client
  clientStatus: ClientStatus | null
  onSubmit: (data: {
    id_client: number
    amount: number
    points: number
    transaction_type: 'spend'
    id_branch_office: number
    comment: string
  }) => void
  isSubmitting: boolean
  error?: string | null
}

export function RedeemPointForm({
  client,
  clientStatus,
  onSubmit,
  isSubmitting,
  error,
}: RedeemPointFormProps) {
  const { branches } = useBranchOffices()
  const { config } = useConfig()
  const [quantities, setQuantities] = useState<QuantityMap>({})
  const [isProductsOpen, setIsProductsOpen] = useState(false)
  const [productSearch, setProductSearch] = useState('')

  const availablePoints = clientStatus?.available_points ?? 0
  const minExchangePoints = config?.min_exchange_points ?? 0
  const canRedeem = availablePoints >= minExchangePoints
  const products = config?.products ?? []

  // Derived data from quantities + catalog
  const selectedItems = products
    .filter((p) => (quantities[p.sku] ?? 0) > 0)
    .map((p) => ({ ...p, quantity: quantities[p.sku] }))

  const totalPoints = selectedItems.reduce(
    (sum, item) => sum + item.points * item.quantity,
    0,
  )

  const totalUnits = selectedItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  )

  const commentFromProducts = selectedItems
    .map((item) => (item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name))
    .join(' + ')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RedeemPointFormData>({
    resolver: zodResolver(redeemPointFormSchema),
    defaultValues: {
      id_client: client.id,
      id_branch_office: client.id_branch_office,
    },
  })

  const clpEquivalent = useMemo(() => {
    if (!config?.value_point_to_CLP || !totalPoints) return 0
    return totalPoints * config.value_point_to_CLP
  }, [totalPoints, config])

  const filteredProducts = useMemo(() => {
    const sorted = [...products].sort((a, b) => a.points - b.points)
    if (!productSearch.trim()) return sorted
    const query = productSearch.toLowerCase()
    return sorted.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        String(p.sku).toLowerCase().includes(query),
    )
  }, [products, productSearch])

  function addOne(sku: string) {
    setQuantities((prev) => ({ ...prev, [sku]: (prev[sku] ?? 0) + 1 }))
  }

  function removeOne(sku: string) {
    setQuantities((prev) => {
      const next = (prev[sku] ?? 0) - 1
      if (next <= 0) {
        const { [sku]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [sku]: next }
    })
  }

  function removeAll(sku: string) {
    setQuantities((prev) => {
      const { [sku]: _, ...rest } = prev
      return rest
    })
  }

  function handleClear() {
    setQuantities({})
  }

  function canAddProduct(productPoints: number) {
    return canRedeem && availablePoints >= productPoints + totalPoints
  }

  const hasProducts = selectedItems.length > 0

  const handleFormSubmit = (data: RedeemPointFormData) => {
    onSubmit({
      id_client: client.id,
      amount: clpEquivalent,
      points: totalPoints,
      transaction_type: 'spend',
      id_branch_office: data.id_branch_office,
      comment: commentFromProducts,
    })
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 max-w-2xl">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Client info */}
      <div className="card p-4 bg-gray-50">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-500">Cliente</p>
            <p className="font-semibold">
              {client.name} {client.last_name} ({client.rut})
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Puntos disponibles</p>
            <p className="text-2xl font-bold text-primary-600">
              {availablePoints - totalPoints}
              {totalPoints > 0 && (
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {availablePoints}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {!canRedeem && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          El cliente necesita al menos {minExchangePoints} puntos para canjear.
        </div>
      )}

      {/* Products catalog */}
      {products.length > 0 && (
        <div className="card">
          <button
            type="button"
            onClick={() => setIsProductsOpen(!isProductsOpen)}
            className="flex items-center justify-between w-full p-4 text-left"
          >
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">
                Productos disponibles ({products.length})
              </h3>
              {totalUnits > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary-600 text-white text-xs font-bold">
                  {totalUnits}
                </span>
              )}
            </div>
            {isProductsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {isProductsOpen && (
            <div className="px-4 pb-4">
              {/* Product search */}
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Buscar producto por nombre o SKU..."
                  className="input pl-9"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <p className="text-sm text-gray-500 col-span-2 py-3 text-center">
                    No se encontraron productos
                  </p>
                ) : (
                  filteredProducts.map((product) => {
                    const qty = quantities[product.sku] ?? 0
                    const isSelected = qty > 0
                    const canAdd = canAddProduct(product.points)

                    return (
                      <div
                        key={product.sku}
                        className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                          isSelected
                            ? 'border-primary-300 bg-primary-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{product.name}</p>
                          <p className="text-sm text-gray-500">
                            SKU: {product.sku} - {product.points} pts
                          </p>
                        </div>

                        {isSelected ? (
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => removeOne(product.sku)}
                              className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <Minus size={14} />
                            </button>
                            <span className="w-8 text-center text-sm font-semibold">
                              {qty}
                            </span>
                            <button
                              type="button"
                              onClick={() => addOne(product.sku)}
                              disabled={!canAdd}
                              className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => addOne(product.sku)}
                            disabled={!canAdd}
                            className="btn btn-secondary text-sm disabled:opacity-40 disabled:cursor-not-allowed ml-2 shrink-0"
                          >
                            Agregar
                          </button>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected products summary */}
      {hasProducts && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">
              Resumen del canje ({totalUnits} {totalUnits === 1 ? 'producto' : 'productos'})
            </h3>
            <button
              type="button"
              onClick={handleClear}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Limpiar todo
            </button>
          </div>
          <div className="space-y-2">
            {selectedItems.map((item) => (
              <div
                key={item.sku}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{item.name}</p>
                  <p className="text-xs text-gray-500">
                    {item.points} pts c/u
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => removeOne(item.sku)}
                      className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => addOne(item.sku)}
                      disabled={!canAddProduct(item.points)}
                      className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 w-16 text-right">
                    {(item.points * item.quantity).toLocaleString('es-CL')} pts
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAll(item.sku)}
                    className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                    title="Eliminar producto"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Total puntos</span>
            <span className="text-lg font-bold text-primary-600">
              {totalPoints.toLocaleString('es-CL')} pts
            </span>
          </div>
          {config && clpEquivalent > 0 && (
            <p className="text-sm text-gray-500 text-right mt-1">
              Equivalente: ${clpEquivalent.toLocaleString('es-CL')} CLP
            </p>
          )}
        </div>
      )}

      {!hasProducts && canRedeem && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          Seleccione al menos un producto del catalogo para canjear.
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal *</label>
        <select
          {...register('id_branch_office', { valueAsNumber: true })}
          className="input"
        >
          <option value="">Seleccionar sucursal</option>
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.commune} - {branch.address}
            </option>
          ))}
        </select>
        {errors.id_branch_office && (
          <p className="text-red-500 text-sm mt-1">{errors.id_branch_office.message}</p>
        )}
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isSubmitting || !canRedeem || !hasProducts || totalPoints > availablePoints}
          className="btn btn-primary disabled:opacity-50"
        >
          {isSubmitting ? 'Canjeando...' : 'Canjear Puntos'}
        </button>
        {hasProducts && (
          <button type="button" onClick={handleClear} className="btn btn-secondary">
            Limpiar datos
          </button>
        )}
      </div>
    </form>
  )
}
